"use client";

// Comments on the grading rubric itself — one thread per rubric VERSION — and,
// since the Weekly Report's skills got pages of their own, on those documents
// too: one thread per (DOCUMENT, VERSION), anchored to a region of it.
//
// ⭐ ONE STORE, SEVERAL DOCUMENTS, AND THE DOCUMENT DECIDES THE KIND. The rubric's
// rows are `rubric_comment`, written only by those who hold Script Grading's
// page (/skills/script-grading); the Weekly Report skills' are `skill_comment`
// (lib/route-access.ts STORE_KIND_ROUTE). Two kinds and not one because the POST
// gate is per kind and each follows its own page. Since 2026-09-22 the two pages
// hold the same roles (AM Ops and the admin; an AM once wrote the skills' notes),
// but they are still two answers that may part again, and every stored row
// already carries its kind. A third, `affiliate_skill_comment`, is the DM Replies
// skill's, for the same reason; a fourth, `client_slack_skill_comment`, client
// Slack's skills'. Every one of those pages is under /skills now, so today all
// four kinds answer to the same two roles; the kinds stay, as stored rows'.
//
// ⚠️ A ROW WITH NO `doc` IS A RUBRIC COMMENT, which is every row written before
// the field existed and every rubric row since (a rubric row never carries it,
// so its shape is unchanged). And the KIND is the authority over `doc`, never the
// other way round: a `skill_comment` row saying `doc: "grading-rubric"` is
// dropped on hydration, or anyone who may write a skill note could post into the
// rubric's thread.
//
// The script threads (ironside-script-store §Comment threads) are coordination
// between the people working a script. These are something else: an argument
// about the standard. That difference is why they are their own store rather
// than a fifth audience on the script one —
//
//   · they key on a rubric version, not a scriptId, and a version is frozen the
//     moment it ships, so a v4 comment must keep pointing at v4's text forever
//     even after v6 is active;
//   · they have no audience axis — the rubric page is Admin/AM only, so there is
//     exactly one thread per version and no Creator/Client toggle to get wrong;
//   · they are FEEDBACK. The feedback console reads this same store, which is
//     what makes a note left on the rubric show up on the triage page without a
//     second copy of it existing anywhere.
//
// What they DO share with script comments is the anchor: `field` here is a
// rubric region id (`item-H1`, `cat-hook`, `intro`) instead of a DocSection id,
// and everything downstream — resolveAnchor, the paint coordinator, the
// blockquote fallback, canModifyComment — is untouched by the difference.

import { uuidv7 } from "uuidv7";
import { persist, isConfirmed } from "./store-persist";
import { sameData } from "./same-data";
import type { CommentAnchor } from "./ironside-script-store";

/** The documents a note can be left on. */
export type CommentDoc = "grading-rubric" | "weekly-draft" | "weekly-check" | "dm-replies" | "client-slack-triage" | "client-slack-items" | "client-slack-segment" | "client-slack-answered" | "client-slack-setaside" | "client-slack-respond" | "client-slack-item-kind" | "client-slack-items-check" | "client-slack-recheck";

export const RUBRIC_DOC: CommentDoc = "grading-rubric";

/** A store kind a document's notes are written as. */
export type CommentKind = "rubric_comment" | "skill_comment" | "affiliate_skill_comment" | "client_slack_skill_comment";

/**
 * Each document's store kind. The rubric's is its own; the Weekly Report's two
 * skills share one. The DM Replies skill has its own because its page was
 * another subtree's (/brands/affiliates/skills, before every skill page moved
 * to /skills), and the POST gate asks the page a KIND belongs to:
 * sharing `skill_comment` would have let whoever held the weekly skills write
 * notes on it, whatever the affiliate pages' own gate said.
 */
export const COMMENT_DOC_KIND: Readonly<Record<CommentDoc, CommentKind>> = {
  "grading-rubric": "rubric_comment",
  "weekly-draft": "skill_comment",
  "weekly-check": "skill_comment",
  "dm-replies": "affiliate_skill_comment",
  // Client Slack's two skills share one: one subtree, one gate.
  "client-slack-triage": "client_slack_skill_comment",
  "client-slack-items": "client_slack_skill_comment",
  "client-slack-segment": "client_slack_skill_comment",
  "client-slack-answered": "client_slack_skill_comment",
  "client-slack-setaside": "client_slack_skill_comment",
  "client-slack-respond": "client_slack_skill_comment",
  "client-slack-item-kind": "client_slack_skill_comment",
  "client-slack-items-check": "client_slack_skill_comment",
  "client-slack-recheck": "client_slack_skill_comment",
};

export interface RubricComment {
  id: string; // uuidv7 — time-ordered, so it self-sorts chronologically
  /** The document. Absent means the grading rubric (see the header). */
  doc?: CommentDoc;
  /** The version of the document this note is about — named for the first
   *  document, the rubric, and kept so stored rows keep their meaning. Comments
   *  never migrate forward: v5's text is not v6's, so a v5 note read against v6
   *  would be a quote out of context — the version picker is how you go and read
   *  it where it applies. */
  rubricVersion: number;
  /** The signed-in email. Comparing display names breaks the moment two people share one. */
  authorId: string;
  authorName: string;
  /** The role the author actually HOLDS, not the lens they were wearing. Tooltip only. */
  authorRole: string;
  text: string;
  postedAtMs: number;
  /** Presence renders "(edited)". */
  editedAtMs?: number;
  /** Tombstone, kept in the row so the text survives for audit. /api/store's DELETE refuses comment kinds. */
  deleted: boolean;
  /** Optional region anchor. Absent = a note about the version as a whole. */
  anchor?: CommentAnchor;
}

export interface NewRubricComment {
  /** Default: the grading rubric. */
  doc?: CommentDoc;
  rubricVersion: number;
  authorId: string;
  authorName: string;
  authorRole: string;
  text: string;
  anchor?: CommentAnchor;
}

// Keyed by comment id — one store row per comment, so two people commenting on
// the same version at the same time merge instead of clobbering.
const comments = new Map<string, RubricComment>();
const listeners = new Set<() => void>();
let version = 0;

function notify(): void {
  version++;
  listeners.forEach((l) => l());
}

export const docOf = (c: Pick<RubricComment, "doc">): CommentDoc => c.doc ?? RUBRIC_DOC;

function write(c: RubricComment): void {
  comments.set(c.id, c);
  // Literal calls, not persist(COMMENT_DOC_KIND[…]): tests/unit/store-kind-route.test.ts
  // finds the kinds the app writes by reading these call sites.
  const kind = COMMENT_DOC_KIND[docOf(c)];
  if (kind === "rubric_comment") persist("rubric_comment", c.id, c);
  else if (kind === "affiliate_skill_comment") persist("affiliate_skill_comment", c.id, c);
  else if (kind === "client_slack_skill_comment") persist("client_slack_skill_comment", c.id, c);
  else persist("skill_comment", c.id, c);
  notify();
}

/** Throws on an empty authorId — see addComment in ironside-script-store for
 *  why: an empty one makes canModifyComment match everyone against everyone. */
export function addRubricComment(input: NewRubricComment): void {
  if (!input.authorId) throw new Error("addRubricComment: authorId is required");
  if (!input.text.trim()) return;
  const doc = input.doc ?? RUBRIC_DOC;
  write({
    id: uuidv7(),
    // Only a skill's note names its document: a rubric row keeps the shape it has always had.
    ...(doc === RUBRIC_DOC ? {} : { doc }),
    rubricVersion: input.rubricVersion,
    authorId: input.authorId,
    authorName: input.authorName,
    authorRole: input.authorRole,
    text: input.text.trim(),
    postedAtMs: Date.now(),
    deleted: false,
    anchor: input.anchor,
  });
}

export function editRubricComment(id: string, text: string): void {
  const existing = comments.get(id);
  if (!existing || existing.deleted || !text.trim()) return;
  write({ ...existing, text: text.trim(), editedAtMs: Date.now() });
}

/** A tombstone, not a delete — /api/store's DELETE refuses comment kinds. */
export function deleteRubricComment(id: string): void {
  const existing = comments.get(id);
  if (!existing || existing.deleted) return;
  write({ ...existing, deleted: true });
}

/** One document version's thread, oldest first (uuidv7 sorts chronologically). */
export function getRubricComments(rubricVersion: number, doc: CommentDoc = RUBRIC_DOC): RubricComment[] {
  return Array.from(comments.values())
    .filter((c) => docOf(c) === doc && c.rubricVersion === rubricVersion && !c.deleted)
    .sort((a, b) => a.id.localeCompare(b.id));
}

/** Every live comment on the grading rubric, newest first — the feedback
 *  console's view, where a note is one item in a triage list rather than one
 *  turn in a conversation. The skills' notes are not the console's. */
export function getAllRubricComments(): RubricComment[] {
  return Array.from(comments.values())
    .filter((c) => docOf(c) === RUBRIC_DOC && !c.deleted)
    .sort((a, b) => b.id.localeCompare(a.id));
}

/** Per-version counts for the version picker — `v5 · 3`. */
export function getRubricCommentCounts(doc: CommentDoc = RUBRIC_DOC): Record<number, number> {
  const out: Record<number, number> = {};
  for (const c of comments.values()) {
    if (c.deleted || docOf(c) !== doc) continue;
    out[c.rubricVersion] = (out[c.rubricVersion] ?? 0) + 1;
  }
  return out;
}

export function subscribeRubricComments(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getRubricCommentsVersion(): number {
  return version;
}

// ─── Hydration from the server ───────────────────────────────────────────────
export function applyServerRows(
  rows: { kind: string; id: string; data: unknown }[],
  deleted: { kind: string; id: string }[] = [],
): void {
  let changed = false;
  // A key deleted on the server leaves the map — only from the kind that holds
  // it, by the same rule as a row: the kind is the authority.
  for (const d of deleted) {
    const held = comments.get(d.id);
    if (!held || COMMENT_DOC_KIND[docOf(held)] !== d.kind) continue;
    if (!isConfirmed(d.kind, d.id, null)) continue;
    comments.delete(d.id);
    changed = true;
  }
  for (const r of rows) {
    if (r.kind !== "rubric_comment" && r.kind !== "skill_comment" && r.kind !== "affiliate_skill_comment" && r.kind !== "client_slack_skill_comment") continue;
    const c = r.data as RubricComment;
    // The kind is the authority over the document (the header's ⚠️).
    const doc = c && typeof c === "object" ? c.doc ?? RUBRIC_DOC : null;
    if (doc === null || !Object.hasOwn(COMMENT_DOC_KIND, doc) || COMMENT_DOC_KIND[doc] !== r.kind) continue;
    if (!isConfirmed(r.kind, r.id, r.data)) continue;
    if (comments.has(r.id) && sameData(comments.get(r.id), c)) continue; // nothing new
    comments.set(r.id, c);
    changed = true;
  }
  if (changed) notify();
}
